<template>
  <button
    class="btn"
    :class="{
      'btn--loading': loading,
      ['btn--variant-' + variant]: variant,
      'btn--icon-only': isIconOnly,
      ['btn--size-' + size]: size,
    }"
    @click="handleClick"
    :disabled="isDisabled || loading"
    :type="type"
  >
    <LoadingSpinner
      v-if="loading"
      :color="['primary', 'secondary'].includes(variant) ? 'light' : 'dark'"
    />
    <div class="btn__content">
      <span v-if="hasSlotContent || text">
        <slot>{{ text }}</slot>
      </span>
      <BaseIcon v-if="icon" :name="icon" :size="size === 'lg' ? '14' : '12'" />
    </div>
  </button>
</template>

<script>
  import { computed } from 'vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  export default {
    name: 'ButtonEl',
    props: {
      text: {
        type: String,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      icon: {
        type: String,
      },
      variant: {
        type: String,
        default: 'primary',
      },
      type: {
        type: String,
        default: 'submit',
      },
      size: {
        type: String,
        default: 'md',
        validator: (value) => ['xs', 'sm', 'md', 'lg'].includes(value),
      },
    },
    components: {
      LoadingSpinner,
      BaseIcon,
    },
    emits: ['click'],
    setup(props, { emit, slots }) {
      const isDisabled = computed(() => (props.loading || props.disabled ? true : false));
      // Check if slot has actual content (not just empty/whitespace)
      const hasSlotContent = computed(() => {
        if (!slots.default) return false;
        const slotContent = slots.default();
        if (!slotContent || slotContent.length === 0) return false;
        
        // Check if any vnode has actual content
        return slotContent.some((vnode) => {
          // Text node with content
          if (typeof vnode.children === 'string') {
            return vnode.children.trim().length > 0;
          }
          // Component or element with children
          if (vnode.children && vnode.children.length > 0) return true;
          // Has a type that's not just text/comment
          if (vnode.type && typeof vnode.type !== 'symbol') return true;
          return false;
        });
      });
      
      const isIconOnly = computed(() => !props.text && !hasSlotContent.value && !!props.icon);
      const handleClick = (event) => {
        emit('click', event);
      };
      return {
        isDisabled,
        handleClick,
        isIconOnly,
        hasSlotContent,
      };
    },
  };
</script>

<style lang="scss" scoped>
  @import '@/assets/scss/base/mixins';
  .btn {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    padding-top: 6px;
    padding-bottom: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    border-width: 1px;
    border-color: transparent;
    border-style: solid;
    font-size: 0.875rem;
    line-height: 1.5;
    font-weight: 500;
    box-shadow:
      0 0 #0000,
      0 0 #0000,
      0 1px 2px 0 rgb(0 0 0 / 0.05);
    background-color: var(--brand-500);
    color: #ffffff;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    @include hover {
    }
    &__content {
      transition: padding 150ms ease-out;
      display: flex;
      align-items: center;
      white-space: nowrap;
      .icon {
        margin-left: 0.5rem;
      }
    }
    &--loading {
      .btn__content {
        padding-left: 0.5rem;
      }
    }
    &--icon-only {
      padding-left: 0.5rem;
      padding-right: 0.5rem;
      .btn__content {
        .icon {
          margin: 0;
        }
      }
    }
    &--size- {
      &lg {
        padding: .6125rem;
      }
    }
    &:disabled {
      opacity: 0.8;
      pointer-events: none;
    }
    &--variant- {
      &primary {
        @include hover {
          background-color: var(--brand-600);
          color: var(--white);
        }
      }
      &danger {
        background-color: var(--white);
        border-color: var(--slate-200);
        color: var(--red-500);
        @include hover {
          border-color: var(--red-500);
        }
      }
      &warning {
        background-color: var(--white);
        border-color: var(--slate-200);
        color: var(--red-500);
        @include hover {
          border-color: var(--red-500);
        }
      }
      &plain {
        background-color: transparent;
        color: var(--slate-500);
        box-shadow: none;
        @include hover {
          color: var(--slate-800);
        }
        // padding-left: 0;
        // padding-right: 0;
      }
      &secondary {
        background-color: var(--slate-800);
        color: var(--slate-100);
        box-shadow: none;
        @include hover {
          background-color: var(--slate-700);
          color: var(--slate-100);
        }
      }
      &tertiary {
        background-color: var(--slate-100);
        border-color: var(--slate-200);
        color: var(--slate-600);
        @include hover {
          color: var(--slate-800);
          background-color: var(--slate-200);
        }
      }
      &outlined {
        background-color: var(--white);
        color: var(--slate-600);
        border-color: var(--slate-300);
        box-shadow: none;
        @include hover {
          color: var(--slate-700);
          border-color: var(--slate-700);
        }
      }
      &success {
        background-color: var(--green-500);
        color: var(--slate-100);
      }
      &dashed {
        background-color: transparent;
        border: 2px dashed var(--slate-300, #e2e8f0);
        color: var(--slate-600, #64748b);
        box-shadow: none;
        @include hover {
          background-color: var(--slate-100, #f8fafc);
          border-color: var(--brand-500, #3b82f6);
          color: var(--brand-500, #3b82f6);
        }
      }
    }
  }
</style>
